import { useState, useEffect } from 'react'
import {
  Container,
  Title,
  Stack,
  Grid,
  Card,
  Text,
  Group,
  Button,
  Loader,
  Center,
  Badge,
  Table,
  ScrollArea,
  TextInput,
  MultiSelect,
  Select,
  Progress,
  Paper,
  RingProgress,
  Pagination,
  Tooltip,
  UnstyledButton,
  ActionIcon,
} from '@mantine/core'
import { DatePickerInput } from '@mantine/dates'
import { IconCalendar, IconRefresh, IconSearch, IconEye, IconEyeOff } from '@tabler/icons-react'
import { useIdentityMatchDashboard } from '@/hooks/useAnalytics'
import { getIdentityMatchSSN } from '@/services/analytics.api'
import { useVersionContext } from '@/context/VersionContext'
import { BarChart } from '@mantine/charts'

const fmt = (v: number | null | undefined) => (v ?? 0).toLocaleString()

// Safe percentage: returns 0 (never NaN/Infinity) when the denominator is 0/missing.
const pct = (n: number | null | undefined, d: number | null | undefined) => {
  const denom = Number(d) || 0
  return denom > 0 ? ((Number(n) || 0) / denom) * 100 : 0
}

function RevealSSN({ hasSSN, matchId, field }: { hasSSN?: boolean; matchId?: string; field: 'candidate_ssn' | 'subject_ssn' }) {
  const [ssnValue, setSsnValue] = useState<string | null>(null)
  const [revealed, setRevealed] = useState(false)
  const [loading, setLoading] = useState(false)

  if (!hasSSN) return <Text size="xs">None</Text>

  const handleReveal = async () => {
    if (revealed) {
      setRevealed(false)
      return
    }
    if (ssnValue !== null) {
      setRevealed(true)
      return
    }
    if (!matchId) return
    setLoading(true)
    try {
      const data = await getIdentityMatchSSN(matchId)
      setSsnValue(data[field] ?? null)
      setRevealed(true)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return <Loader size={14} />
  }

  if (revealed && ssnValue) {
    return (
      <Tooltip label="Click to hide" withArrow>
        <UnstyledButton onClick={handleReveal}>
          <Group gap={4} wrap="nowrap">
            <Text size="xs" ff="monospace">{ssnValue}</Text>
            <IconEyeOff size={14} style={{ opacity: 0.5 }} />
          </Group>
        </UnstyledButton>
      </Tooltip>
    )
  }

  return (
    <Tooltip label="Click to reveal SSN" withArrow>
      <ActionIcon variant="subtle" size="sm" onClick={handleReveal}>
        <IconEye size={14} />
      </ActionIcon>
    </Tooltip>
  )
}

export function IdentityMatchDashboard() {
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
    new Date(),
  ])
  const [searchId, setSearchId] = useState('')
  const [caseNumber, setCaseNumber] = useState('')
  const [matchLevelFilter, setMatchLevelFilter] = useState<string[]>([])
  const [isMatchFilter, setIsMatchFilter] = useState<string[]>([])
  const [subjectName, setSubjectName] = useState<string | null>(null)
  const [subjectDob, setSubjectDob] = useState<string | null>(null)
  const [subjectSsn, setSubjectSsn] = useState<string | null>(null)
  const [subjectAddress, setSubjectAddress] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const pageSize = 50

  const subjectDataFilter = [subjectName, subjectDob, subjectSsn, subjectAddress].filter(Boolean) as string[]

  const { selectedVersion } = useVersionContext()

  const { data: dashboard, isLoading, refetch } = useIdentityMatchDashboard({
    startDate: dateRange[0]?.toISOString().split('T')[0] || '',
    endDate: dateRange[1]?.toISOString().split('T')[0] || '',
    page,
    pageSize,
    searchId,
    caseNumber,
    matchLevel: matchLevelFilter,
    isMatch: isMatchFilter,
    subjectDataFilter,
    modelVersion: selectedVersion || undefined,
  })

  useEffect(() => {
    setPage(1)
  }, [dateRange, searchId, caseNumber, matchLevelFilter, isMatchFilter, subjectName, subjectDob, subjectSsn, subjectAddress, selectedVersion])

  const rows = dashboard?.rows || []
  const totalCount = dashboard?.totalCount || 0
  const stats = totalCount > 0 ? dashboard?.metrics || null : null
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize))

  if (isLoading && !dashboard) {
    return (
      <Center h={400}>
        <Stack align="center" gap="md">
          <Loader size="lg" />
          <Text c="dimmed">Loading identity match data...</Text>
        </Stack>
      </Center>
    )
  }

  return (
    <Container size="xl" py="md">
      <Stack gap="lg">
        <Group justify="space-between" align="center">
          <div>
            <Title order={2}>Identity Verification</Title>
            <Text c="dimmed" size="sm">
              Candidate identity matching confidence and scoring
            </Text>
          </div>
          <Button leftSection={<IconRefresh size={16} />} onClick={() => refetch()} variant="light" size="sm">
            Refresh
          </Button>
        </Group>

        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <DatePickerInput
            type="range"
            label="Search Date Range"
            placeholder="Select date range"
            value={dateRange}
            onChange={setDateRange}
            leftSection={<IconCalendar size={16} />}
            clearable
          />
        </Card>

        {stats && (
          <>
            <Title order={3}>Summary</Title>
            <Grid>
              <Grid.Col span={3}>
                <Card shadow="sm" padding="lg" radius="md" withBorder>
                  <Stack align="center" gap="xs">
                    <Text size="2.5rem" fw={700} c="brand">{fmt(stats.totalCases)}</Text>
                    <Text size="sm" c="dimmed">Total Cases</Text>
                  </Stack>
                </Card>
              </Grid.Col>
              <Grid.Col span={3}>
                <Card shadow="sm" padding="lg" radius="md" withBorder>
                  <Stack align="center" gap="xs">
                    <Text size="2.5rem" fw={700} c="teal">{fmt(stats.highMatch)}</Text>
                    <Text size="sm" c="dimmed">High Match ({pct(stats.highMatch, stats.totalCases).toFixed(1)}%)</Text>
                  </Stack>
                </Card>
              </Grid.Col>
              <Grid.Col span={3}>
                <Card shadow="sm" padding="lg" radius="md" withBorder>
                  <Stack align="center" gap="xs">
                    <Text size="2.5rem" fw={700} c="yellow.7">{fmt(stats.mediumMatch)}</Text>
                    <Text size="sm" c="dimmed">Medium Match ({pct(stats.mediumMatch, stats.totalCases).toFixed(1)}%)</Text>
                  </Stack>
                </Card>
              </Grid.Col>
              <Grid.Col span={3}>
                <Card shadow="sm" padding="lg" radius="md" withBorder>
                  <Stack align="center" gap="xs">
                    <Text size="2.5rem" fw={700} c="red">{fmt(stats.notMatching)}</Text>
                    <Text size="sm" c="dimmed">Not Matching ({pct(stats.notMatching, stats.totalCases).toFixed(1)}%)</Text>
                  </Stack>
                </Card>
              </Grid.Col>
            </Grid>

            <Title order={3}>ID Match Distribution</Title>
            <Grid>
              {/* Pie Chart */}
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Card shadow="sm" padding="lg" radius="md" withBorder>
                  <Stack gap="md" align="center">
                    <Text fw={600} size="lg">Match Level Distribution</Text>
                    <RingProgress
                      size={280}
                      thickness={40}
                      sections={[
                        {
                          value: pct(stats.highMatch, stats.totalCases),
                          color: '#2ECC71',
                          tooltip: `High: ${fmt(stats.highMatch)} (${pct(stats.highMatch, stats.totalCases).toFixed(1)}%)`
                        },
                        {
                          value: pct(stats.mediumMatch, stats.totalCases),
                          color: '#F39C12',
                          tooltip: `Medium: ${fmt(stats.mediumMatch)} (${pct(stats.mediumMatch, stats.totalCases).toFixed(1)}%)`
                        },
                        {
                          value: pct(stats.notMatching, stats.totalCases),
                          color: '#E74C3C',
                          tooltip: `Not Matching: ${fmt(stats.notMatching)} (${pct(stats.notMatching, stats.totalCases).toFixed(1)}%)`
                        },
                        {
                          value: pct(stats.notEnoughInfo, stats.totalCases),
                          color: '#95A5A6',
                          tooltip: `Not Enough Info: ${fmt(stats.notEnoughInfo)} (${pct(stats.notEnoughInfo, stats.totalCases).toFixed(1)}%)`
                        },
                      ]}
                      label={
                        <Stack align="center" gap={0}>
                          <Text size="2xl" fw={700}>{fmt(stats.totalCases)}</Text>
                          <Text size="xs" c="dimmed">Total Cases</Text>
                        </Stack>
                      }
                    />
                    <Stack gap="xs" style={{ width: '100%' }}>
                      <Group justify="space-between">
                        <Group gap="xs">
                          <div style={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#2ECC71' }} />
                          <Text size="sm">High</Text>
                        </Group>
                        <Text size="sm" fw={600}>
                          {fmt(stats.highMatch)} ({pct(stats.highMatch, stats.totalCases).toFixed(1)}%)
                        </Text>
                      </Group>
                      <Group justify="space-between">
                        <Group gap="xs">
                          <div style={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#F39C12' }} />
                          <Text size="sm">Medium</Text>
                        </Group>
                        <Text size="sm" fw={600}>
                          {fmt(stats.mediumMatch)} ({pct(stats.mediumMatch, stats.totalCases).toFixed(1)}%)
                        </Text>
                      </Group>
                      <Group justify="space-between">
                        <Group gap="xs">
                          <div style={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#E74C3C' }} />
                          <Text size="sm">Not Matching</Text>
                        </Group>
                        <Text size="sm" fw={600}>
                          {fmt(stats.notMatching)} ({pct(stats.notMatching, stats.totalCases).toFixed(1)}%)
                        </Text>
                      </Group>
                      <Group justify="space-between">
                        <Group gap="xs">
                          <div style={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#95A5A6' }} />
                          <Text size="sm">Not Enough Info</Text>
                        </Group>
                        <Text size="sm" fw={600}>
                          {fmt(stats.notEnoughInfo)} ({pct(stats.notEnoughInfo, stats.totalCases).toFixed(1)}%)
                        </Text>
                      </Group>
                    </Stack>
                  </Stack>
                </Card>
              </Grid.Col>

              {/* Bar Chart */}
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Card shadow="sm" padding="lg" radius="md" withBorder>
                  <Stack gap="md">
                    <Text fw={600} size="lg">Match Count by Level</Text>
                    <BarChart
                      h={350}
                      data={[
                        { level: 'High', count: stats.highMatch, color: '#2ECC71' },
                        { level: 'Medium', count: stats.mediumMatch, color: '#F39C12' },
                        { level: 'Not Matching', count: stats.notMatching, color: '#E74C3C' },
                        { level: 'Not Enough Info', count: stats.notEnoughInfo, color: '#95A5A6' },
                      ]}
                      dataKey="level"
                      series={[{ name: 'count', color: 'blue' }]}
                      tickLine="y"
                      withLegend={false}
                      yAxisProps={{
                        label: { value: 'Number of Cases', angle: -90, position: 'insideLeft' }
                      }}
                      xAxisProps={{
                        label: { value: 'Match Level', position: 'insideBottom', offset: -5 }
                      }}
                    />
                  </Stack>
                </Card>
              </Grid.Col>
            </Grid>

            <Title order={3}>Average Match Scores</Title>
            <Grid>
              <Grid.Col span={6}>
                <Paper p="md" withBorder>
                  <Stack gap="xs">
                    <Text size="sm" fw={500}>Overall Match Score</Text>
                    <Progress value={stats.avgOverall * 100} size="xl" color="blue" />
                    <Text size="lg" fw={700}>{(stats.avgOverall * 100).toFixed(1)}%</Text>
                  </Stack>
                </Paper>
              </Grid.Col>
              <Grid.Col span={6}>
                <Grid>
                  <Grid.Col span={6}>
                    <Paper p="sm" withBorder>
                      <Text size="xs" c="dimmed">SSN Score</Text>
                      <Text size="lg" fw={700}>{(stats.avgSsn * 100).toFixed(1)}%</Text>
                    </Paper>
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <Paper p="sm" withBorder>
                      <Text size="xs" c="dimmed">DOB Score</Text>
                      <Text size="lg" fw={700}>{(stats.avgDob * 100).toFixed(1)}%</Text>
                    </Paper>
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <Paper p="sm" withBorder>
                      <Text size="xs" c="dimmed">Name Score</Text>
                      <Text size="lg" fw={700}>{(stats.avgName * 100).toFixed(1)}%</Text>
                    </Paper>
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <Paper p="sm" withBorder>
                      <Text size="xs" c="dimmed">Address Score</Text>
                      <Text size="lg" fw={700}>{(stats.avgAddress * 100).toFixed(1)}%</Text>
                    </Paper>
                  </Grid.Col>
                </Grid>
              </Grid.Col>
            </Grid>

          </>
        )}

        <Title order={3}>Case-Level Explorer</Title>
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Stack gap="md">
            <Grid>
              <Grid.Col span={{ base: 12, md: 3 }}>
                <TextInput
                  label="Search ID"
                  placeholder="Enter search_id..."
                  leftSection={<IconSearch size={16} />}
                  value={searchId}
                  onChange={(e) => setSearchId(e.target.value)}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 3 }}>
                <TextInput
                  label="Case Number"
                  placeholder="Enter case number..."
                  leftSection={<IconSearch size={16} />}
                  value={caseNumber}
                  onChange={(e) => setCaseNumber(e.target.value)}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 3 }}>
                <MultiSelect
                  label="Identity Level"
                  placeholder="All levels"
                  data={[
                    { value: 'High', label: 'High' },
                    { value: 'Medium', label: 'Medium' },
                    { value: 'Not Enough Info', label: 'Not Enough Info' },
                    { value: 'Not Matching', label: 'Not Matching' },
                  ]}
                  value={matchLevelFilter}
                  onChange={setMatchLevelFilter}
                  clearable
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 3 }}>
                <MultiSelect
                  label="Is Match"
                  placeholder="All"
                  data={[
                    { value: 'Match', label: 'Match' },
                    { value: 'No Match', label: 'No Match' },
                  ]}
                  value={isMatchFilter}
                  onChange={setIsMatchFilter}
                  clearable
                />
              </Grid.Col>
            </Grid>
            <Grid>
              <Grid.Col span={{ base: 6, md: 3 }}>
                <Select
                  label="Subject Name"
                  placeholder="All"
                  data={[
                    { value: 'name_available', label: 'Available' },
                    { value: 'name_missing', label: 'Missing' },
                  ]}
                  value={subjectName}
                  onChange={setSubjectName}
                  clearable
                />
              </Grid.Col>
              <Grid.Col span={{ base: 6, md: 3 }}>
                <Select
                  label="Subject DOB"
                  placeholder="All"
                  data={[
                    { value: 'dob_available', label: 'Available' },
                    { value: 'dob_missing', label: 'Missing' },
                  ]}
                  value={subjectDob}
                  onChange={setSubjectDob}
                  clearable
                />
              </Grid.Col>
              <Grid.Col span={{ base: 6, md: 3 }}>
                <Select
                  label="Subject SSN"
                  placeholder="All"
                  data={[
                    { value: 'ssn_available', label: 'Available' },
                    { value: 'ssn_missing', label: 'Missing' },
                  ]}
                  value={subjectSsn}
                  onChange={setSubjectSsn}
                  clearable
                />
              </Grid.Col>
              <Grid.Col span={{ base: 6, md: 3 }}>
                <Select
                  label="Subject Address"
                  placeholder="All"
                  data={[
                    { value: 'address_available', label: 'Available' },
                    { value: 'address_missing', label: 'Missing' },
                  ]}
                  value={subjectAddress}
                  onChange={setSubjectAddress}
                  clearable
                />
              </Grid.Col>
            </Grid>

            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                Showing {rows.length.toLocaleString()} of {fmt(totalCount)} cases (Page {page} of {totalPages})
              </Text>
              {totalPages > 1 && (
                <Pagination
                  total={totalPages}
                  value={page}
                  onChange={setPage}
                  size="sm"
                />
              )}
            </Group>

            {rows.length > 0 ? (
              <>
                <ScrollArea>
                  <Table striped highlightOnHover withTableBorder withColumnBorders>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Search ID</Table.Th>
                        <Table.Th>Case Number</Table.Th>
                        <Table.Th>Candidate First Name</Table.Th>
                        <Table.Th>Candidate Last Name</Table.Th>
                        <Table.Th>Candidate DOB</Table.Th>
                        <Table.Th>Candidate SSN</Table.Th>
                        <Table.Th>Subject First Name</Table.Th>
                        <Table.Th>Subject Last Name</Table.Th>
                        <Table.Th>Subject DOB</Table.Th>
                        <Table.Th>Subject SSN</Table.Th>
                        <Table.Th>Subject Address</Table.Th>
                        <Table.Th>Match Level</Table.Th>
                        <Table.Th>Is Match</Table.Th>
                        <Table.Th>Match Score</Table.Th>
                        <Table.Th>SSN Score</Table.Th>
                        <Table.Th>DOB Score</Table.Th>
                        <Table.Th>Name Score</Table.Th>
                        <Table.Th>Address Score</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {rows.map((record, index) => (
                        <Table.Tr key={`${record.search_id}-${index}`}>
                          <Table.Td>
                            <Text size="xs" ff="monospace">{record.search_id || 'None'}</Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="xs">{record.case_number || 'None'}</Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="xs">{record.candidate_first_name || 'None'}</Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="xs">{record.candidate_last_name || 'None'}</Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="xs">{record.candidate_dob || 'None'}</Text>
                          </Table.Td>
                          <Table.Td>
                            <RevealSSN hasSSN={record.has_candidate_ssn} matchId={record.match_id} field="candidate_ssn" />
                          </Table.Td>
                          <Table.Td>
                            <Text size="xs">{record.subject_first_name || 'None'}</Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="xs">{record.subject_last_name || 'None'}</Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="xs">{record.subject_dob || 'None'}</Text>
                          </Table.Td>
                          <Table.Td>
                            <RevealSSN hasSSN={record.has_subject_ssn} matchId={record.match_id} field="subject_ssn" />
                          </Table.Td>
                          <Table.Td>
                            <Text size="xs">{record.subject_address || 'None'}</Text>
                          </Table.Td>
                          <Table.Td>
                            <Badge
                              size="sm"
                              color={
                                record.match_level === 'High' ? 'green' :
                                record.match_level === 'Medium' ? 'orange' :
                                record.match_level === 'Not Matching' ? 'red' :
                                record.match_level === 'Not Enough Info' ? 'yellow' : 'gray'
                              }
                            >
                              {record.match_level}
                            </Badge>
                          </Table.Td>
                          <Table.Td>
                            <Badge
                              size="sm"
                              color={record.is_match ? 'green' : 'red'}
                            >
                              {record.is_match ? 'Match' : 'No Match'}
                            </Badge>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm">{((record.match_score || 0) * 100).toFixed(1)}%</Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm">{((record.ssn_score || 0) * 100).toFixed(1)}%</Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm">{((record.dob_score || 0) * 100).toFixed(1)}%</Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm">{((record.name_score || 0) * 100).toFixed(1)}%</Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm">{((record.address_score || 0) * 100).toFixed(1)}%</Text>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </ScrollArea>

                {totalPages > 1 && (
                  <Group justify="center">
                    <Pagination
                      total={totalPages}
                      value={page}
                      onChange={setPage}
                    />
                  </Group>
                )}
              </>
            ) : (
              <Center h={150}>
                <Stack align="center" gap="sm">
                  <Text c="dimmed">No data for the selected filters</Text>
                  {dashboard?.availableDateRange && (
                    <Text size="sm" c="blue">
                      Available data range: {dashboard.availableDateRange.minDate} to {dashboard.availableDateRange.maxDate}
                    </Text>
                  )}
                </Stack>
              </Center>
            )}
          </Stack>
        </Card>
      </Stack>
    </Container>
  )
}
